// src/pages/cyclecount/CycleCountManagement.jsx
import React, { useState, useEffect, useRef } from "react";
import { toast } from "../../components/AppToast";
import CycleCountImportList from "./CycleCountImportList";
import CreateCycleCount from "./CreateCycleCount";
import { fetchCycleCountViewById } from "../../services/cyclecount/cyclecount";


export default function CycleCountImportManagement({
    deepLinkCycleCountId,
    initialCycleCountId
} = {}) {
    const cycleCountLinkId = initialCycleCountId ?? deepLinkCycleCountId;

    const [isCreating, setIsCreating] = useState(false);
    const [activeRecord, setActiveRecord] = useState(null); // view/edit
    const deepLinkHandledRef = useRef(null);

    const onlyFormVisible = isCreating || !!activeRecord;

    useEffect(() => {
        if (!cycleCountLinkId) return;
        if (deepLinkHandledRef.current === cycleCountLinkId) return;
        deepLinkHandledRef.current = cycleCountLinkId;

        (async () => {
            try {
                const apiData = await fetchCycleCountViewById(cycleCountLinkId);
                if (apiData) {
                    setActiveRecord({ mode: "view", data: apiData });
                } else {
                    toast.error("Could not load cycle count details.");
                }
            } catch (e) {
                toast.error(
                    typeof e === "string" ? e : e?.message || "Failed to open cycle count"
                );
            }
        })();
    }, [cycleCountLinkId]);

    const closeForm = () => {
        setIsCreating(false);
        setActiveRecord(null);
    };

    return (
        <div className="card shadow-lg main-card">

            {/* ---------- HEADER ---------- */}
            <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
                <h4 className="mb-0 h6 fw-semibold">
                    {!onlyFormVisible
                        ? "Cycle Count"
                        : isCreating
                            ? "Create Cycle Count"
                            : activeRecord?.mode === "edit"
                                ? "Edit Cycle Count"
                                : "View Cycle Count"
                    }
                </h4>
            </div>

            {/* ---------- BREADCRUMBS ---------- */}
            {onlyFormVisible && (
                <nav
                    aria-label="breadcrumb"
                    className="px-3 py-2 border-bottom bg-light"
                >
                    <ol
                        className="breadcrumb mb-0 align-items-center"
                        style={{ ["--bs-breadcrumb-divider"]: "'/'" }}
                    >
                        <li className="breadcrumb-item">
                            <button
                                type="button"
                                className="btn btn-link p-0 text-decoration-none text-muted"
                                onClick={closeForm}
                            >
                                Cycle Count
                            </button>
                        </li>

                        <li
                            className="breadcrumb-item active fw-semibold text-primary"
                            aria-current="page"
                        >
                            {isCreating
                                ? "Create Cycle Count"
                                : activeRecord?.mode === "edit"
                                    ? "Edit Cycle Count"
                                    : "View Cycle Count"}
                        </li>
                    </ol>
                </nav>
            )}

            {/* ---------- BODY ---------- */}
            <div className="card-body">
                {!onlyFormVisible ? (
                    <CycleCountImportList
                        onCreateClick={() => setIsCreating(true)}
                        onView={async (id) => {
                            const apiData = await fetchCycleCountViewById(id);

                            setActiveRecord({
                                mode: "view",
                                data: apiData      // 👈 API KA FULL RESPONSE
                            });
                        }}
                        onEdit={(row) =>
                            setActiveRecord({ mode: "edit", data: row })
                        }
                    />

                ) : (
                    <CreateCycleCount
                        initialValues={activeRecord?.data || {}}
                        onCancel={closeForm}
                        onSubmit={closeForm}
                        viewOnly={activeRecord?.mode === "view"}
                        isEdit={activeRecord?.mode === "edit"}
                    />
                )}
            </div>
        </div>
    );
}
